import { FieldProps, Field } from 'formik'
import React, { memo, FunctionComponent, useMemo, useCallback, useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { DynamicFormFieldProps } from '../../ui/form/DynamicFormField'
import { TranslatedString } from '../../locale'

import DynamicInput from '../../ui/form/DynamicInput'
import Label from '../../ui/form/Label'
import FormField from '../../ui/form/FormField'
import DynamicFormFieldType from '../../ui/form/DynamicFormFieldType'
import TextInput from '../../ui/form/TextInput'
import CheckoutStepType from '../../checkout/CheckoutStepType';

import storage from '../utils/storage'
import { tips } from '../locales'


const AddressLabelField: FunctionComponent<
  DynamicFormFieldProps & {
    b3Company: any
    type?: CheckoutStepType
  }
> = ({
  field: basicField,
  parentFieldName,
  placeholder,
  autocomplete,
  inputId,
  b3Company,
  type,
}) => {
    const {
      name,
      label,
      required,
      options,
      max,
      min,
      maxLength,
    } = basicField

    const fieldInputId = inputId || name
    const fieldName = parentFieldName ? `${parentFieldName}.${basicField.name}` : basicField.name
    const [companyName, setCompanyName] = useState('')

    useEffect(() => {
      const companyName = b3Company.companyBasicInfo?.companyName
      companyName && setCompanyName(companyName)
      storage.B3FormCompanyName.setValue(companyName)

      return () => {}
    }, [b3Company.companyBasicInfo])

    const labelComponent = useMemo(
      () => (
        <Label htmlFor={fieldInputId}>
          {label}
          {!required && (
            <>
              {' '}
              <small className='optimizedCheckout-contentSecondary'>
                <TranslatedString id='common.optional_text' />
              </small>
            </>
          )}
        </Label>
      ),
      [fieldInputId, label, required, label]
    )

    const getCompanyName = (company: any) => {
      if (!company) return ''
      return company.includes(companyName) ? companyName : company.split('/').reverse()[0]
    }

    const getAddressLabel = (company: string | undefined) => {
      if (!company) return ''

      const currentCompanyName = getCompanyName(company)
      const label = company
        .replace(new RegExp(`^([\\s\\S]*)${currentCompanyName}$`), '$1')
        .split('/')[0]
      return label
    }

    const renderAddressLabel = useCallback(
      ({ field, form }:  FieldProps<string>) => {
        const company: string = (type && type === CheckoutStepType.Billing) 
        ? field.value.company 
        : field.value.shippingAddress.company

        const currentCompanyName = getCompanyName(company)
        const quoteId = localStorage.getItem('quoteCheckoutId') || ''
        const B3QuoteLabel = storage.B3QuoteLabel.getValue()
        return (
          <>
            <Label htmlFor="address-label">
              {tips['b3.addresses.addressLabel']} {tips['b3.label.optional']}
            </Label>
            <TextInput
              name="address-label"
              value={quoteId ? B3QuoteLabel : getAddressLabel(company)}
              onChange={async (e) => {
                const value = e.target.value
                const updatedValue = value ? `${value}/${currentCompanyName}` : currentCompanyName

                if (type && type === CheckoutStepType.Billing) form.setFieldValue('company', updatedValue)
                else form.setFieldValue('shippingAddress.company', updatedValue)
                
                await new Promise(resolve => setTimeout(resolve))
              }}
            />
          </>
        )
      },
      [fieldInputId, max, maxLength, min, options, placeholder, autocomplete, companyName]
    )

    const renderCompanyName = useCallback(
      ({ field, form }: FieldProps<string>) => {
        return (
          <DynamicInput
            {...field}
            value={getCompanyName(field.value)}
            onChange={async (e) => {
              const label = getAddressLabel(field.value)
              const value = e.target.value
              const currentLabel = label ? `${label}/` : ''
              
              if (type && type === CheckoutStepType.Billing) form.setFieldValue('company', `${currentLabel}${value}`)
              else form.setFieldValue('shippingAddress.company', `${currentLabel}${value}`)

              storage.B3FormCompanyName.setValue(value)
              await new Promise(resolve => setTimeout(resolve))
            }}
            autoComplete={autocomplete}
            fieldType={DynamicFormFieldType.text}
            id={fieldInputId}
            max={max}
            maxLength={maxLength || undefined}
            min={min}
            options={options && options.items}
            placeholder={placeholder || (options && options.helperLabel)}
            rows={options && (options as any).rows}
          />
        )
      },
      [fieldInputId, max, maxLength, min, options, placeholder, autocomplete, companyName]
    )

    return (
      <>
        <div className={`dynamic-form-field ${'dynamic-form-field--b3AddressLabel'}`}>
          <Field render={renderAddressLabel} />
        </div>
        <div className={`dynamic-form-field ${'dynamic-form-field--b3Company'}`}>
          <FormField
            input={renderCompanyName}
            label={labelComponent}
            name={fieldName}
          />
        </div>
      </>
    )
  }

const mapStateToProps = (state: { company: any }) => {
  const {
    company: b3Company
  } = state

  return {
    b3Company,
  }
}

export default memo(connect(mapStateToProps)(AddressLabelField))
